import type { Localized } from './notice'

export type VariableKind = 'system' | 'custom'

/**
 * A value that can be dropped into any channel's content as a chip.
 * Variables live only for the duration of the notice being composed — they are
 * never persisted to the module configuration.
 */
export interface NoticeVariable {
  id: string
  /** Display name shown inside the chip and in the variables block. */
  label: Localized
  kind: VariableKind
  /**
   * Value substituted at send time.
   * - custom: the exact text typed by the author, per content language.
   * - system: a representative sample used for the preview only.
   */
  value: { fr: string; en: string }
  /**
   * Shown when the citizen record has no value for this variable.
   * Only relevant for per-citizen system variables.
   */
  fallback?: { fr: string; en: string }
  /** How a system variable is resolved. Absent for custom variables. */
  auto?: 'citizen' | 'date'
}

/** Localized copy explaining where a system variable's value comes from. */
export const SYSTEM_VARIABLE_SOURCE: Record<string, Localized> = {
  'sys.prenom': { fr: 'Fiche citoyen bciti+', en: 'bciti+ citizen record' },
  'sys.nom': { fr: 'Fiche citoyen bciti+', en: 'bciti+ citizen record' },
  'sys.date': { fr: "Date d'envoi de l'avis", en: 'Notice sending date' },
}

/** The only three variables resolved automatically by the platform. */
export const SYSTEM_VARIABLES: NoticeVariable[] = [
  {
    id: 'sys.prenom',
    label: { fr: 'Prénom du citoyen', en: "Citizen's first name" },
    kind: 'system',
    auto: 'citizen',
    value: { fr: 'Marie', en: 'Marie' },
    fallback: { fr: 'citoyen·ne', en: 'citizen' },
  },
  {
    id: 'sys.nom',
    label: { fr: 'Nom du citoyen', en: "Citizen's last name" },
    kind: 'system',
    auto: 'citizen',
    value: { fr: 'Tremblay', en: 'Tremblay' },
    fallback: { fr: '', en: '' },
  },
  {
    id: 'sys.date',
    label: { fr: 'Date du jour', en: "Today's date" },
    kind: 'system',
    auto: 'date',
    value: { fr: '', en: '' },
  },
]
